// Gaps service for CareerCatalyst
// Handles gap analysis and recommendation operations

import { api } from './api';
import type { Gap } from '../types';

// Gap request types
export interface GapAnalysisRequest {
  job_posting_id?: string;
  target_role?: string;
  target_company?: string;
  job_description?: string;
  requirements?: string[];
}

export interface CreateGapRequest {
  type: 'knowledge' | 'goal';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  recommendation: string;
  affected_elements: string[];
  impact_score: number;
}

export interface UpdateGapRequest {
  type?: 'knowledge' | 'goal';
  severity?: 'low' | 'medium' | 'high' | 'critical';
  description?: string;
  recommendation?: string;
  affected_elements?: string[];
  impact_score?: number;
}

export interface GapListResponse {
  items: Gap[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface GapAnalysisResponse {
  analysis_id: string;
  job_posting_id?: string;
  gaps: Gap[];
  overall_match_percentage: number;
  recommendations: Array<{
    category: string;
    priority: 'high' | 'medium' | 'low';
    description: string;
    action_items: string[];
  }>;
  skill_gaps: Array<{
    skill_name: string;
    required_level: number;
    current_level: number;
    gap_size: number;
  }>;
  experience_gaps: Array<{
    area: string;
    description: string;
    suggested_actions: string[];
  }>;
}

export interface RemediationPlanRequest {
  gap_ids: string[];
  timeline?: string;
  priority_order?: 'impact' | 'difficulty' | 'time';
}

export interface RemediationPlan {
  plan_id: string;
  gaps: Gap[];
  timeline: string;
  completion_percentage?: number;
  phases: Array<{
    phase_number: number;
    duration: string;
    objectives: string[];
    actions: Array<{
      type: 'skill_development' | 'experience' | 'certification' | 'project';
      description: string;
      estimated_effort: string;
      resources: string[];
    }>;
  }>;
  estimated_completion: string;
  success_metrics: string[];
}

// GapsService class
class GapsService {
  /**
   * Get all gaps for current user
   */
  async getGaps(page: number = 1, size: number = 50): Promise<GapListResponse> {
    const gaps = await api.get<Gap[]>(`/gaps/?page=${page}&size=${size}`);
    // Backend returns array directly, wrap in GapListResponse format
    return {
      items: gaps,
      total: gaps.length,
      page: page,
      size: size,
      pages: 1
    };
  }

  /**
   * Get gap by ID
   */
  async getGapById(gapId: string): Promise<Gap> {
    return api.get<Gap>(`/gaps/${gapId}`);
  }

  /**
   * Create new gap
   */
  async createGap(gapData: CreateGapRequest): Promise<Gap> {
    return api.post<Gap>('/gaps/', gapData);
  }

  /**
   * Update gap
   */
  async updateGap(gapId: string, gapData: UpdateGapRequest): Promise<Gap> {
    return api.patch<Gap>(`/gaps/${gapId}`, gapData);
  }

  /**
   * Get gaps by severity level
   */
  async getGapsBySeverity(severity: 'low' | 'medium' | 'high' | 'critical', page: number = 1, size: number = 50): Promise<GapListResponse> {
    const gaps = await api.get<Gap[]>(`/gaps/?severity=${severity}&page=${page}&size=${size}`);
    return {
      items: gaps,
      total: gaps.length,
      page: page,
      size: size,
      pages: 1
    };
  }

  /**
   * Get gaps by type (returns actionable gaps: identified, in_progress, paused)
   */
  async getGapsByType(type: 'knowledge' | 'goal', page: number = 1, size: number = 50): Promise<GapListResponse> {
    // Fetch all gaps of this type, then filter to actionable statuses
    // This ensures gaps don't disappear when user pauses a session
    const allGaps = await api.get<Gap[]>(`/gaps/?gap_type=${type}&page=${page}&size=${size}`);

    // Filter to only actionable statuses (exclude resolved/dismissed)
    const actionableGaps = allGaps.filter(gap =>
      gap.status === 'identified' ||
      gap.status === 'in_progress' ||
      gap.status === 'paused'
    );

    return {
      items: actionableGaps,
      total: actionableGaps.length,
      page: page,
      size: size,
      pages: 1
    };
  }

  /**
   * Perform gap analysis against job posting
   */
  async analyzeGaps(analysisRequest: GapAnalysisRequest): Promise<GapAnalysisResponse> {
    return api.post<GapAnalysisResponse>('/gaps/analyze', analysisRequest);
  }

  /**
   * Get gap analysis by ID
   */
  async getGapAnalysis(analysisId: string): Promise<GapAnalysisResponse> {
    return api.get<GapAnalysisResponse>(`/gaps/analysis/${analysisId}`);
  }

  /**
   * Create remediation plan for gaps
   */
  async createRemediationPlan(planRequest: RemediationPlanRequest): Promise<RemediationPlan> {
    return api.post<RemediationPlan>('/gaps/remediation-plan', planRequest);
  }

  /**
   * Get remediation plan by ID
   */
  async getRemediationPlan(planId: string): Promise<RemediationPlan> {
    return api.get<RemediationPlan>(`/gaps/remediation-plan/${planId}`);
  }

  /**
   * Update remediation plan progress
   */
  async updateRemediationProgress(planId: string, progress: {
    phase_number: number;
    completed_actions: string[];
    notes?: string;
  }): Promise<RemediationPlan> {
    return api.patch<RemediationPlan>(`/gaps/remediation-plan/${planId}/progress`, progress);
  }

  /**
   * Get gap recommendations based on user profile
   */
  async getGapRecommendations(): Promise<{
    recommendations: Array<{
      category: string;
      priority: 'high' | 'medium' | 'low';
      description: string;
      potential_impact: string;
    }>;
  }> {
    return api.get('/gaps/recommendations');
  }

  /**
   * Mark gap as addressed/resolved
   */
  async markGapResolved(gapId: string, resolution_notes?: string): Promise<Gap> {
    return api.patch<Gap>(`/gaps/${gapId}/resolve`, {
      resolution_notes
    });
  }

  /**
   * Reopen a resolved gap
   */
  async reopenGap(gapId: string, reason?: string): Promise<Gap> {
    return api.patch<Gap>(`/gaps/${gapId}/reopen`, {
      reason
    });
  }

  /**
   * Submit evidence for gap remediation
   */
  async submitEvidence(gapId: string, evidence: {
    artifact_id: string;
    description: string;
  }): Promise<Gap> {
    return api.post<Gap>(`/gaps/${gapId}/evidence`, evidence);
  }

  /**
   * Start knowledge gap remediation session
   */
  async startRemediationSession(gapId: string): Promise<any> {
    return api.post<any>(`/gaps/${gapId}/start-remediation`, {});
  }
}

// Export singleton instance
export const gapsService = new GapsService();
